import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';
import './Profiles.css';

// Submódulo "Historial de Placas": placas asignadas/usadas por cada concesionario y su origen
// (solicitud de rango, asignación directa de TransiApp o pool general).
const filterInputStyle = {
  padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: '10px',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500', outline: 'none', width: '100%'
};
const labelStyle = { fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase' };

const ORIGENES = {
  solicitud: { label: 'Solicitud de rango', color: '#15803d', bg: '#dcfce7' },
  asignacion_directa: { label: 'Asignación directa TransiApp', color: '#1d4ed8', bg: '#dbeafe' },
  pool_general: { label: 'Pool general TransiApp', color: '#b45309', bg: '#fef3c7' },
  otro_concesionario: { label: 'Placa de otro concesionario', color: '#b91c1c', bg: '#fee2e2' }
};
const TIPOS = { moto: 'Moto', moto_carro: 'Moto Carro', carro: 'Carro' };
const ESTADOS_PLACA = { disponible: 'Disponible', asignada: 'Asignada', pre_asignada: 'Pre-asignada', consumida: 'Consumida' };

const formatFecha = (d) => d ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const toBogotaDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }) : '';

const HistorialPlacas = () => {
  const { user } = useContext(AuthContext);
  const isTransiapp = ['superadmin', 'admin'].includes(user?.role) || ['superadmin', 'admin', 'auxiliar transiapp'].includes(user?.profile_name);

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtroConcesionario, setFiltroConcesionario] = useState('todos');
  const [filtroTransito, setFiltroTransito] = useState('todos');
  const [filtroOrigen, setFiltroOrigen] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get('/api/plate-history');
        setRegistros(Array.isArray(res.data) ? res.data : []);
        setError('');
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Error al cargar el historial de placas.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const options = (keyId, keyName) => {
    const map = new Map();
    registros.forEach(r => { if (r[keyId]) map.set(String(r[keyId]), r[keyName] || `#${r[keyId]}`); });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  };
  const concesionarioOptions = useMemo(() => options('concesionario_id', 'concesionario_name'), [registros]); // eslint-disable-line react-hooks/exhaustive-deps
  const transitoOptions = useMemo(() => options('transito_id', 'transito_name'), [registros]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => registros.filter(r => {
    if (filtroConcesionario !== 'todos' && String(r.concesionario_id) !== filtroConcesionario) return false;
    if (filtroTransito !== 'todos' && String(r.transito_id) !== filtroTransito) return false;
    if (filtroOrigen !== 'todos' && r.origen !== filtroOrigen) return false;
    if (filtroTipo !== 'todos' && r.vehicle_type !== filtroTipo) return false;
    const f = toBogotaDate(r.fecha_referencia);
    if (fechaInicio && f < fechaInicio) return false;
    if (fechaFin && f > fechaFin) return false;
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      const hay = [r.plate_number, r.tramite_id, r.solicitud_id, r.tramite_usuario]
        .some(v => String(v || '').toLowerCase().includes(term));
      if (!hay) return false;
    }
    return true;
  }), [registros, filtroConcesionario, filtroTransito, filtroOrigen, filtroTipo, fechaInicio, fechaFin, searchTerm]);

  const resumen = useMemo(() => {
    const r = { solicitud: 0, asignacion_directa: 0, pool_general: 0, otro_concesionario: 0 };
    filtered.forEach(x => { r[x.origen] = (r[x.origen] || 0) + 1; });
    return r;
  }, [filtered]);

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = filtered.slice(indexOfLast - itemsPerPage, indexOfLast);

  const exportToExcel = () => {
    if (filtered.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    const data = filtered.map(r => ({
      'Placa': r.plate_number,
      'Tipo de Vehículo': TIPOS[r.vehicle_type] || r.vehicle_type || '—',
      'Tránsito': r.transito_name || '—',
      'Concesionario': r.concesionario_name || '—',
      'Origen': ORIGENES[r.origen]?.label || r.origen,
      'Solicitud de Rango': r.solicitud_id ? `#${r.solicitud_id}` : '—',
      'Fecha Asignación': formatFecha(r.fecha_asignacion),
      'Dueño de la Placa': r.concesionario_duenio_placa || (r.origen === 'pool_general' ? 'TransiApp' : r.concesionario_name || '—'),
      'Estado Placa': ESTADOS_PLACA[r.plate_status] || r.plate_status || '—',
      'Trámite': r.tramite_id ? `#${r.tramite_id}` : 'Sin usar',
      'Estado Trámite': r.tramite_estado || '—',
      'Fecha Trámite': formatFecha(r.tramite_fecha),
      'Usuario Trámite': r.tramite_usuario || '—'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 10 }, { wch: 13 }, { wch: 16 }, { wch: 32 }, { wch: 28 }, { wch: 12 }, { wch: 18 }, { wch: 28 }, { wch: 13 }, { wch: 10 }, { wch: 24 }, { wch: 18 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial Placas');
    const conc = filtroConcesionario !== 'todos'
      ? (concesionarioOptions.find(([id]) => id === filtroConcesionario)?.[1] || 'concesionario')
      : (isTransiapp ? 'todos' : (filtered[0]?.concesionario_name || 'concesionario'));
    XLSX.writeFile(wb, `Historial_Placas_${conc.replace(/[^A-Za-z0-9]+/g, '_')}_${new Date().toLocaleDateString('en-CA')}.xlsx`);
  };

  const resetPage = (fn) => (e) => { fn(e.target.value); setCurrentPage(1); };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🗂️ Historial de Placas" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="header-title">
              <h2>Historial de Placas por Concesionario</h2>
              <p className="subtitle">Placas asignadas o usadas y su origen ({filtered.length})</p>
            </div>
            <button
              onClick={exportToExcel}
              style={{ padding: '10px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
            >
              📥 Descargar Excel
            </button>
          </div>

          {/* Resumen por origen (clic para filtrar) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {Object.entries(ORIGENES).map(([key, o]) => (
              <button
                key={key}
                type="button"
                onClick={() => { setFiltroOrigen(filtroOrigen === key ? 'todos' : key); setCurrentPage(1); }}
                aria-pressed={filtroOrigen === key}
                style={{
                  textAlign: 'left', padding: '12px 16px', borderRadius: '12px', cursor: 'pointer',
                  background: o.bg, color: o.color, border: filtroOrigen === key ? `2px solid ${o.color}` : '2px solid transparent'
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 700 }}>{o.label}</div>
                <div style={{ fontSize: '22px', fontWeight: 800 }}>{resumen[key] || 0}</div>
              </button>
            ))}
          </div>

          <div className="dashboard-filters" style={{
            background: 'var(--card-bg)', padding: '16px 24px', borderRadius: '14px', marginBottom: '20px',
            boxShadow: 'var(--card-shadow)', border: '1px solid var(--card-border)',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px'
          }}>
            {isTransiapp && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="h-conc" style={labelStyle}>Concesionario</label>
                <select id="h-conc" value={filtroConcesionario} onChange={resetPage(setFiltroConcesionario)} style={filterInputStyle}>
                  <option value="todos">Todos</option>
                  {concesionarioOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-transito" style={labelStyle}>Tránsito</label>
              <select id="h-transito" value={filtroTransito} onChange={resetPage(setFiltroTransito)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                {transitoOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-origen" style={labelStyle}>Origen</label>
              <select id="h-origen" value={filtroOrigen} onChange={resetPage(setFiltroOrigen)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                {Object.entries(ORIGENES).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-tipo" style={labelStyle}>Tipo de Vehículo</label>
              <select id="h-tipo" value={filtroTipo} onChange={resetPage(setFiltroTipo)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                {Object.entries(TIPOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-desde" style={labelStyle}>Desde</label>
              <input id="h-desde" type="date" value={fechaInicio} onChange={resetPage(setFechaInicio)} style={filterInputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-hasta" style={labelStyle}>Hasta</label>
              <input id="h-hasta" type="date" value={fechaFin} onChange={resetPage(setFechaFin)} style={filterInputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="h-buscar" style={labelStyle}>Buscar</label>
              <input id="h-buscar" type="text" placeholder="Placa, trámite, solicitud..." value={searchTerm} onChange={resetPage(setSearchTerm)} style={filterInputStyle} />
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando historial de placas...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Placa</th>
                      <th>Tipo</th>
                      <th>Tránsito</th>
                      {isTransiapp && <th>Concesionario</th>}
                      <th>Origen</th>
                      <th>Solicitud</th>
                      <th>Fecha Asignación</th>
                      <th>Estado Placa</th>
                      <th>Trámite</th>
                      <th>Fecha Trámite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map(r => {
                      const o = ORIGENES[r.origen] || { label: r.origen, color: '#334155', bg: '#f1f5f9' };
                      return (
                        <tr key={`${r.plate_id}-${r.origen}-${r.concesionario_id}`}>
                          <td data-label="Placa"><span className="plate-badge-sm">{r.plate_number}</span></td>
                          <td data-label="Tipo">{TIPOS[r.vehicle_type] || r.vehicle_type || '—'}</td>
                          <td data-label="Tránsito">{r.transito_name || '—'}</td>
                          {isTransiapp && <td data-label="Concesionario">{r.concesionario_name || '—'}</td>}
                          <td data-label="Origen">
                            <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: o.color, background: o.bg, whiteSpace: 'nowrap' }}>
                              {o.label}
                            </span>
                          </td>
                          <td data-label="Solicitud">{r.solicitud_id ? `#${r.solicitud_id}` : '—'}</td>
                          <td data-label="Fecha Asignación">{formatFecha(r.fecha_asignacion)}</td>
                          <td data-label="Estado Placa">{ESTADOS_PLACA[r.plate_status] || r.plate_status || '—'}</td>
                          <td data-label="Trámite">{r.tramite_id ? <strong>#{r.tramite_id}</strong> : <span style={{ color: '#94a3b8' }}>Sin usar</span>}</td>
                          <td data-label="Fecha Trámite">{formatFecha(r.tramite_fecha)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No hay placas con los filtros seleccionados.
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalItems={filtered.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistorialPlacas;
